import { inject, Injectable } from '@angular/core';
import { VARIABLES } from '@environments/environment.variables';
import { DataCache } from '@services/cache/models';
import { ContextService } from '@services/context';
import localforage from 'localforage';
import { default as packageInfo } from 'package.json';
import { EMPTY } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class CacheService {
  private readonly contextService = inject(ContextService);
  private readonly INSTANCE_NAME = packageInfo.name;
  private readonly BUILD_TIMESTAMP = VARIABLES.buildTimestamp;

  constructor() {
    if (this.contextService.isServer()) return;
    localforage.config({
      name: this.INSTANCE_NAME,
    });
  }

  /**
   * Récupération des données
   * @param key Clé de récupération des données
   */
  public get(key: string): Promise<unknown> {
    if (this.contextService.isServer()) return Promise.resolve(EMPTY);
    return localforage.getItem(key).then((cache: unknown) => {
      const dataCache = cache as DataCache;
      if (dataCache && dataCache?.buildTimestamp !== this.BUILD_TIMESTAMP) {
        void this.clear();
        return null;
      }
      if (dataCache?.expiredAt === 0 || (dataCache?.expiredAt > 0 && dataCache?.expiredAt >= Date.now())) {
        return dataCache.data;
      }
      return null;
    });
  }

  /**
   * Enregistrement des données dans la base
   * @param key Clé pour l'enregistrement
   * @param value Valeur à enregistrer
   * @param expirationTime Temps d'expiration des données en secondes (0: illimité)
   */
  public set(key: string, value: unknown, expirationTime = 0): Promise<unknown> {
    if (this.contextService.isServer()) return Promise.resolve(EMPTY);
    return localforage.setItem(key, new DataCache(value, expirationTime)).then((valueSaved) => valueSaved.data);
  }

  /**
   * Suppression d'une clé
   * @param key Clé à supprimer
   */
  public remove(key: string): Promise<void> {
    if (this.contextService.isServer()) return Promise.resolve();
    return localforage.removeItem(key);
  }

  /**
   * Nettoyage complet de la base des clés
   */
  public clear(): Promise<void> {
    if (this.contextService.isServer()) return Promise.resolve();
    return localforage.clear();
  }

  /**
   * Retourne la liste des clés
   */
  public listKeys(): Promise<string[]> {
    if (this.contextService.isServer()) return Promise.resolve([]);
    return localforage.keys();
  }

  /**
   * Supprime la base de donnée
   */
  public dropInstance(): Promise<void> {
    return localforage.dropInstance({
      name: this.INSTANCE_NAME,
    });
  }
}
